import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Users, Search, AlertTriangle, CalendarClock, FileText } from 'lucide-react';

/**
 * My Participants — Support-Worker-safe list backed by
 * GET /api/staff/my-participants (assignment-filtered, summary projection
 * only). No clinical, address or contact data in the list payload.
 */
export default function MyParticipantsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 12;

  const load = useCallback(async (q = search, p = page) => {
    try {
      setError(null); setDenied(false);
      const r = await api.get('/staff/my-participants', { params: { search: q || undefined, page: p, page_size: pageSize } });
      setData(r.data.data);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      else setError(e?.response?.data?.error?.message || 'Unable to load participants');
      setData({ items: [], total: 0 });
    }
  }, [search, page]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); load(search, 1); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const items = data?.items || [];
  const totalPages = Math.max(1, Math.ceil((data?.total || 0) / pageSize));
  const planSoon = (d) => d && new Date(d) < new Date(Date.now() + 60 * 86400000);
  const noteStale = (d) => !d || new Date(d) < new Date(Date.now() - 14 * 86400000);

  return (
    <div className="space-y-5" data-testid="my-participants-page">
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search by name…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="participant-search" aria-label="Search participants" />
      </div>

      {denied && (
        <Card data-testid="participants-denied"><CardContent className="py-10 text-center text-sm text-muted-foreground">You don&apos;t have permission to view participants.</CardContent></Card>
      )}
      {error && !denied && (
        <Card data-testid="participants-error"><CardContent className="py-10 text-center space-y-3">
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button variant="outline" size="sm" onClick={() => load()} data-testid="participants-retry">Retry</Button>
        </CardContent></Card>
      )}
      {data === null && !error && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" aria-busy="true" data-testid="participants-loading">
          {[...Array(6)].map((_, i) => <div key={i} className="h-32 animate-pulse bg-muted rounded-lg" />)}
        </div>
      )}
      {data && !error && !denied && items.length === 0 && (
        <Card data-testid="participants-empty"><CardContent className="py-12 text-center">
          <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="text-muted-foreground font-medium">{search ? 'No participants match your search' : 'No participants assigned to you yet'}</p>
          <p className="text-sm text-muted-foreground mt-1">Participants appear here once you have shifts assigned with them.</p>
        </CardContent></Card>
      )}

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {items.map((p) => (
          <Card key={p.id} className="hover:shadow-md hover:border-primary/40 transition-all cursor-pointer"
                onClick={() => navigate(`/staff/participants/${p.id}`)} data-testid={`participant-card-${p.id}`}
                role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/staff/participants/${p.id}`); }}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center font-semibold" aria-hidden="true">{p.initials}</div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold truncate">{p.first_name} {p.last_name}</h3>
                  <div className="text-xs text-muted-foreground">{p.status || 'Active'}</div>
                </div>
                {p.active_alert_count > 0 && (
                  <Badge className="bg-red-100 text-red-700 hover:bg-red-100 gap-1" data-testid={`participant-alert-${p.id}`}>
                    <AlertTriangle className="h-3 w-3" />{p.active_alert_count}
                  </Badge>
                )}
              </div>
              {(p.support_tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1">{p.support_tags.slice(0, 3).map((t) => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}</div>
              )}
              <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                {p.ndis_plan_end_date && (
                  <span className={`inline-flex items-center gap-1 ${planSoon(p.ndis_plan_end_date) ? 'text-amber-700 font-medium' : ''}`}>
                    <CalendarClock className="h-3 w-3" />Plan ends {p.ndis_plan_end_date}{planSoon(p.ndis_plan_end_date) ? ' (soon)' : ''}
                  </span>
                )}
                <span className={`inline-flex items-center gap-1 ${noteStale(p.last_note_at) ? 'text-amber-700' : ''}`}>
                  <FileText className="h-3 w-3" />
                  {p.last_note_at ? `Last note ${new Date(p.last_note_at).toLocaleDateString()}` : 'No progress note yet'}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3" data-testid="participants-pagination">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="participants-prev">Previous</Button>
          <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="participants-next">Next</Button>
        </div>
      )}
    </div>
  );
}
